import { Validator } from 'fluentvalidation-ts';
import { DataForEditPdfDetailsForm } from './dataForEditPdfDetailsForm';
import {
  beAnInteger,
  beValidDate,
  beGreaterThanOrEqualTo,
  haveDecimalPlaces,
} from '../../../validators/stringValidators';
import { fieldRequiredValidationMessage } from '../../../validators/sharedValidators';
import { UnitHeatSourceTypeCode } from '../../shared/quote-or-design-form/quoteOrDesignFormData';

export type RevisionFormModel = {
  code: string;
  date: string;
  initials: string;
  description: string;
};

export type EditPdfDetailsFormModel = {
  numberOfPagesNotIncluded: string;
  designerInitials: string;
  drawerInitials: string;
  waterBufferSizeInLitres: string;
  boilerCapacityInKilowatts: string;
  revisions: Array<RevisionFormModel>;
};

export const getInitialEditPdfDetailsFormModel = (response: DataForEditPdfDetailsForm): EditPdfDetailsFormModel => ({
  numberOfPagesNotIncluded: response.numberOfPagesNotIncluded.toString(),
  designerInitials: response.designerInitials,
  drawerInitials: response.drawerInitials,
  waterBufferSizeInLitres: response.waterBufferSizeInLitres?.toString() ?? '',
  boilerCapacityInKilowatts: response.boilerCapacityInKilowatts?.toString() ?? '',
  revisions: response.revisions.map(revision => ({
    ...revision,
    date: getDateString(revision.date),
  })),
});

const getDateString = (dateTimeString: string) => {
  const date = new Date(dateTimeString);
  return date.toISOString().split('T')[0];
};

export class EditPdfDetailsFormModelValidator extends Validator<EditPdfDetailsFormModel> {
  constructor(protected unitHeatSourceTypeCode: UnitHeatSourceTypeCode | null) {
    super();

    this.ruleFor('numberOfPagesNotIncluded')
      .notEmpty()
      .must(beAnInteger)
      .must(beGreaterThanOrEqualTo(0));
    this.ruleFor('designerInitials')
      .notEmpty()
      .withMessage(fieldRequiredValidationMessage);
    this.ruleFor('drawerInitials')
      .notEmpty()
      .withMessage(fieldRequiredValidationMessage);
    this.ruleFor('waterBufferSizeInLitres')
      .notEmpty()
      .must(beGreaterThanOrEqualTo(0))
      .must(haveDecimalPlaces(2));
    this.ruleFor('boilerCapacityInKilowatts')
      .notEmpty()
      .must(beGreaterThanOrEqualTo(0))
      .must(haveDecimalPlaces(2))
      .when(() => this.unitHeatSourceTypeCode === 'Boiler');
    this.ruleForEach('revisions').setValidator(() => new RevisionFormModelValidator());
  }
}

export class RevisionFormModelValidator extends Validator<RevisionFormModel> {
  constructor() {
    super();

    this.ruleFor('code')
      .notNull()
      .notEmpty()
      .withMessage(fieldRequiredValidationMessage)
      .maxLength(100)
      .withMessage('Please enter no more than 100 characters');

    this.ruleFor('date')
      .notEmpty()
      .must(beValidDate);

    this.ruleFor('initials')
      .notEmpty()
      .withMessage(fieldRequiredValidationMessage)
      .maxLength(100)
      .withMessage('Please enter no more than 100 characters');

    this.ruleFor('description')
      .notEmpty()
      .withMessage(fieldRequiredValidationMessage)
      .maxLength(100)
      .withMessage('Please enter no more than 100 characters');
  }
}
